import { useMutation, useQueryClient } from '@tanstack/react-query';
import Api, { withAcl } from 'hooks/Api';
import { toastSuccess } from 'ui/partials/Toast';
import { useAdminParams } from 'utils/helpers';
import { useProvider } from 'utils/providers/Provider';
import { ApiError, Deal } from 'utils/types';
import { useTour } from '@reactour/tour';
import partialConstants from 'ui/partials/utils/constants';

import contactsConstants from '../../contact/utils/constants';
import c from '../utils/constants';
import { DEAL_FORM } from '../forms/schema';

export default function useCreateDeal() {
  const queryClient = useQueryClient();
  const { state } = useProvider();
  const { accountId, accountUserId } = useAdminParams();
  const { isOpen, setIsOpen } = useTour();
  const args = state;

  return useMutation<{ data: Deal }, ApiError, DEAL_FORM, any>({
    mutationFn: async (payload) => {
      // Triggers a successful mutation without writing to the DB
      // Only used when a tour is opened
      if (isOpen) {
        setIsOpen(false);
        return { data: {} };
      }

      const api = new Api(args);

      const data = await api.post(
        await withAcl({
          account: {
            url: '/api/v1/sales/deals',
            payload,
          },
          admin: {
            url: `/api/v2/accounts/${accountId}/users/${accountUserId}/sales/deals`,
            payload,
          },
        }),
      );

      return data;
    },
    onSuccess: (successData) => {
      const { data } = successData;

      queryClient.setQueryData([c.PIPELINES, {}], (prevData: any) => {
        const newPipelines = [...prevData];

        const index = newPipelines.findIndex(
          (pipeline) => pipeline._id === data.pipelineId,
        );

        if (index > -1) {
          newPipelines[index].deal = (newPipelines[index].deal ?? 0) + 1;
        }

        return newPipelines;
      });

      queryClient.invalidateQueries({
        queryKey: [c.PIPELINES, data.pipelineId, c.DEALS],
      });
      queryClient.invalidateQueries({
        queryKey: [contactsConstants.LEADS],
      });
      queryClient.invalidateQueries({
        queryKey: [contactsConstants.COMPANIES],
      });
      queryClient.invalidateQueries({
        queryKey: [partialConstants.TAGS],
      });
      queryClient.invalidateQueries([c.DEALS, { keyword: '' }]);

      toastSuccess('Successfully created new deal.');
    },
  });
}
